
import { Component, Ref, Vue, Watch } from "vue-property-decorator"
import { namespace } from "vuex-class"

import { utils } from "@/mixins/utils"

import { HideableLoginOptions, SamlProvider, TenantDomain, DomainLoginOptions } from "@/types/domain"

import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import DataTable from "../DataTable.vue"

import UserRoleTeams from "./UserRoleTeams.vue"

import tenantApi from "@/services/api_Tenant"

import Guid from "@/scripts/guid"
import { Team } from "@/types"

const setup = namespace("setup")

@Component({
	components: {
		"v-select": vSelect,
		scrollbar: VuePerfectScrollbar,
		skeleton: SkeletonLoader,
		"data-table": DataTable
	}
})
export default class DomainSetup extends Vue {
	@setup.Getter("getTeams")
	public teams!: Team[];

	@Ref()
	readonly container!: HTMLDivElement;

	loading: boolean = false
	saving: boolean = false

	domain: TenantDomain | null = null
	domainPathSaved: string | null = null

	imagePath: string | null = null

	uploadFile: File | null = null
	uploadFileBase64: string | null = null
	clearedLogo: boolean = false

	domainInUseCheck: NodeJS.Timeout | null = null
	domainInUse: boolean = false
	checkedDomain: string | null = null

	changesMade: boolean = false

	copied: boolean = false

	private readonly defaultLoginOptions: DomainLoginOptions = {
		hiddenElements: [],
		showSsoBelowLogin: false
	}

	private readonly defaultDomain: TenantDomain = {
		id: null,
		domainPath: "",
		samlProviders: [],
		loginOptions: this.defaultLoginOptions
	}

	private get hideUiElementsOptions (): {id: HideableLoginOptions, label: string}[] {
		const signupEnabled = process.env.VUE_APP_SIGN_UP_ENABLED && process.env.VUE_APP_SIGN_UP_ENABLED.toLowerCase() === "true"

		const result = [
			{
				id: HideableLoginOptions.Logo,
				label: "Logo Image"
			},
			{
				id: HideableLoginOptions.Sso,
				label: "SSO Buttons"
			},
			{
				id: HideableLoginOptions.ForgotPassword,
				label: "Forgot password"
			}
		]

		if (signupEnabled) {
			result.push({
				id: HideableLoginOptions.Signup,
				label: "Sign up"
			})
		}

		return result
	}

	get samlColumns () {
		return [
			{
				key: "redirectUrl",
				label: "Application Callback Url",
				readonly: true,
				inTable: false
			},
			{
				key: "title",
				label: "Name",
				sortable: true,
				errors: (item: any) => item.title ? null : "Please enter a name"
			},
			{
				key: "issuer",
				label: "Issuer",
				description: "SAML Provider Issuer",
				inTable: false
			},
			{
				key: "loginUrl",
				label: "Login Url",
				description: "Url to allow users to log in",
				inTable: false
			},
			{
				key: "metaDataUrl",
				label: "MetaData Url",
				description: "URL to fetch user metadata",
				inTable: false,
				errors: (item: any) => item.metaDataUrl || item.providerCertificate ? null : "You must provide either a meta data URL or a certificate"
			},
			{
				key: "providerCertificate",
				label: "Provider Certificate",
				description: "Provider certificate",
				inTable: false,
				dataType: "text-area",
				errors: (item: any) => item.metaDataUrl || item.providerCertificate ? null : "You must provide either a meta data URL or a certificate"
			},
			{
				key: "allowCreate",
				label: "Create on Login",
				description: "Automatically create new users on first time login",
				dataType: "checkbox",
				inTable: false
			},
			{
				key: "profilePictureClaim",
				label: "Profile Picture SAML Claim",
				description: "SAML Claim for URL to profile picture",
				inTable: false
			},
			{
				key: "roleClaim",
				label: "SAML Claim for user roles",
				description: "Claim for user roles",
				inTable: false
			},
			{
				key: "requiredRole",
				label: "Required User Role",
				description: "User role required to access Cases",
				inTable: false,
				inModal: (item: SamlProvider) => item.roleClaim
			},
			{
				key: "teamRoles",
				label: "Role Teams",
				description: "Teams to assign to based on user roles",
				inTable: false,
				dynamicComponent: UserRoleTeams,
				componentProperties: {
					teams: this.teams
				},
				inModal: (item: SamlProvider) => item.roleClaim
			},
			{
				key: "autoLogin",
				label: "Auto-Login",
				description: "Automatically log in via this when login page loads",
				dataType: "checkbox",
				inTable: false
			},
			{
				key: "handlesMfa",
				label: "Handles Two-Factor Authentication",
				description: "Logging in using this provider will not ask for the user's Two Factor Authentication to be entered",
				dataType: "checkbox",
				inTable: false
			}
		]
	}

	get hasDomain (): boolean {
		return !!this.domain
	}

	set hasDomain (value: boolean) {
		if (value) {
			if (!this.domain) {
				this.domain = this.defaultDomain
			}
		} else {
			this.domain = null
		}
	}

	get hasDomainImage (): boolean {
		if (!this.domain || this.clearedLogo) return false
		return !!this.domain.hasImage || !!this.uploadFileBase64
	}

	get imageSource () : string | null {
		return this.uploadFileBase64
			? this.uploadFileBase64
			: this.domainPathSaved !== null
				? `${process.env.VUE_APP_HTTP_API_URI}tenant/logo/${(this.domainPathSaved === "" ? "default" : this.domainPathSaved)}`
				: null
	}

	get domainPathIsValid () {
		if (!this.domain) return false
		if (this.domainInUse) return false
		if (this.domain.domainPath === "default") return false
		if (this.domain.domainPath && this.domain.domainPath !== this.domainPathSaved && this.checkedDomain !== null && this.checkedDomain !== this.domain.domainPath) return false
		if (!this.domain.domainPath.match("^[a-zA-Z0-9]*$")) return false

		return !!this.domain && ((!!this.domain.domainPath && this.domain.domainPath.length > 3) || (this.domainPathSaved !== null && this.domain.domainPath === this.domainPathSaved))
	}

	get domainPathInvalidReason () {
		if (!this.domain) return null

		return this.domain.domainPath === "default"
			? "Domain path is not allowed"
			: this.domainInUse
				? "Domain path is already in use"
				: this.domain.domainPath.length < 4
					? "Domain path must be at least 4 characters long"
					: this.domain.domainPath.match("^[a-zA-Z0-9]*$")
						? null
						: "Domain path must only use letters and numbers"
	}

	get samlProviders () {
		return this.domain ? this.domain.samlProviders.sortBy("title") : []
	}

	get samlApiPath (): string {
		const dotReplace = `${window.location.protocol}//${window.location.host}`
		return (process.env.VUE_APP_HTTP_API_URI!.replace(".", dotReplace)) + "login/saml"
	}

	get loginUrl () {
		if (!this.domain || !this.domain.domainPath) return ""
		if (!this.domainPathIsValid) return ""

		return `${window.location.protocol}//${window.location.host}/${this.domain.domainPath}`
	}

	async mounted () {
		this.loading = true

		const domain = await tenantApi.getTenantDomain()
		this.domain = domain
		if (this.domain && !this.domain.loginOptions) {
			this.domain.loginOptions = this.defaultLoginOptions
		}

		this.loading = false
		this.changesMade = false
		this.clearedLogo = false

		if (this.domain) {
			this.domainPathSaved = domain!.domainPath
			this.domain.samlProviders.forEach(p => p.redirectUrl = `${this.samlApiPath}/${p.key}`)
		}
	}

	@Watch("uploadFile")
	uploadFileChanged (newFile: File | null) {
		this.changesMade = true
		if (newFile) {
			const reader = new FileReader()
			reader.onloadend = () => {
				this.uploadFileBase64 = (reader.result as string)
			}
			reader.readAsDataURL(newFile)
			this.clearedLogo = false
		} else {
			this.uploadFileBase64 = null
		}
	}

	async save () {
		if (this.saving) {
			return
		}

		this.saving = true

		try {
			await tenantApi.setTenantDomain(this.domain)

			if (this.uploadFile !== null) {
				await tenantApi.uploadDomainFile(this.uploadFile)
				this.domain!.hasImage = true
				this.uploadFile = null
			} else if (this.clearedLogo) {
				await tenantApi.clearDomainFile()
				this.domain!.hasImage = false
			}

			this.domainPathSaved = this.domain ? this.domain.domainPath : null
			this.changesMade = false

			this.$bvToast.toast("Domain changes saved", {
				title: "Saved",
				variant: "success",
				solid: true,
				autoHideDelay: 5000
			})
		} catch (err) {
			this.$bvToast.toast(`Unable to save: ${err}`, {
				title: "Error",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})
		} finally {
			this.saving = false
		}
	}

	saveSaml (samlProvider: SamlProvider) {
		if (!this.domain) {
			return
		}

		if (samlProvider.key) {
			this.domain.samlProviders.removeWhere(p => p.key === samlProvider.key)
		} else {
			samlProvider.key = Guid.newGuid()
		}

		this.domain.samlProviders.push(samlProvider)
		this.changesMade = true
	}

	async validateDomainPath () {
		if (!this.domain || !this.domain.domainPath || !this.domain.domainPath.match("^[a-zA-Z0-9]*$")) {
			return
		}

		if (this.domainInUseCheck) {
			clearTimeout(this.domainInUseCheck)
		}
		this.checkedDomain = null
		this.domainInUseCheck = setTimeout(async () => {
			if (this.domain && this.domainPathSaved !== this.domain!.domainPath) {
				const domainPath = this.domain!.domainPath
				this.domainInUse = await tenantApi.domainInUse(domainPath)
				this.checkedDomain = domainPath
			}
			this.domainInUseCheck = null
		}, 1000)
		this.changesMade = true
	}

	clearLogo () {
		this.uploadFile = null
		this.clearedLogo = true
		this.changesMade = true
	}

	newProvider () {
		const key = Guid.newGuid()
		return {
			key,
			redirectUrl: `${this.samlApiPath}/${key}`
		}
	}

	deleteProvider (samlProvider: SamlProvider) {
		if (!this.domain) {
			return
		}

		if (samlProvider.key) {
			this.domain.samlProviders.removeWhere(p => p.key === samlProvider.key)
		}
	}

	copyLoginUrl () {
		utils.methods.copyToClipboard(this.container, this.loginUrl)
		this.copied = true
		setTimeout(() => {
			this.copied = false
		}, 1000)
	}

	openLogin () {
		window.open(this.loginUrl, "_blank")
	}
}

